// Probe git CLI (yêu cầu >= 2.31 cho `--path-format=absolute`) + resolve
// absolute common git dir cho 1 repo path. THUẦN đúng 1 lệnh git (xem thân
// hàm resolveGitCommonDir), KHÔNG có nhánh fallback (KI-4/B9 — phần
// "git-common-dir trần" trong linked worktree CHƯA ĐO, không code theo giả
// định chưa đo). Ticket #138 phase-01.
import { spawnSync } from "node:child_process";
import { isAbsolute } from "node:path";

export type CompareGitVersionResult = "ok" | "too-old" | "unparsable";

// THUẦN, so bằng CẶP SỐ NGUYÊN (B6) — CẤM ép "X.Y" thành 1 số thực rồi so
// hoặc so chuỗi trực tiếp (phase-01 §Req F3c): ép "2.9" thành số thực cho
// 2.9 > 2.31 ⇒ sẽ báo git 2.9 là "ok" — SAI (git 2.9 cũ hơn 2.31 theo semver).
export function compareGitVersion(raw: string): CompareGitVersionResult {
  const m = /(\d+)\.(\d+)/.exec(raw);
  if (!m) return "unparsable";
  // m[1]/m[2] luôn tồn tại — regex có đúng 2 capture group bắt buộc (không
  // optional) và `m` đã được guard non-null ở trên. noUncheckedIndexedAccess
  // vẫn suy ra `string | undefined` vì không track được số capture group cố
  // định (precedent: detect-gitnexus-installation.ts dùng cùng kiểu `!`).
  const major = Number.parseInt(m[1]!, 10);
  const minor = Number.parseInt(m[2]!, 10);
  return major > 2 || (major === 2 && minor >= 31) ? "ok" : "too-old";
}

export interface ProbeGitCliResult {
  status: "ok" | "unavailable" | "too-old";
  version?: string;
}

// Cache module-level: chạy `git --version` MỘT lần cho cả run (theo thuật
// toán phase-01 §Architecture). Test cần isolation giữa các case → dùng
// vi.resetModules() + dynamic import trong resolve-git-common-dir.test.ts.
let cachedProbe: ProbeGitCliResult | undefined;

export function probeGitCli(): ProbeGitCliResult {
  if (cachedProbe) return cachedProbe;

  const result = spawnSync("git", ["--version"], { encoding: "utf8" });
  if (result.error || result.status !== 0) {
    cachedProbe = { status: "unavailable" };
    return cachedProbe;
  }

  const raw = result.stdout ?? "";
  const cmp = compareGitVersion(raw);
  const versionMatch = /\d+\.\d+(?:\.\d+)?/.exec(raw);
  const version = versionMatch?.[0];

  if (cmp === "unparsable") {
    // Không đoán — coi như git không dùng được (F3c: call site map sang
    // "unavailable", không tự suy "ok").
    cachedProbe = { status: "unavailable" };
  } else if (cmp === "too-old") {
    cachedProbe = { status: "too-old", version };
  } else {
    cachedProbe = { status: "ok", version };
  }
  return cachedProbe;
}

// Ném ra khi lệnh git resolve common dir (xem resolveGitCommonDir bên dưới)
// trả `status === 0` nhưng output BẤT THƯỜNG (nhiều dòng / không phải
// absolute path) — ĐO ĐƯỢC trên git < 2.31 (D6): git không nhận flag lạ thì
// echo lại chính nó rồi in tiếp path TƯƠNG ĐỐI, vẫn thoát rc=0. Không
// validate ⇒ chuỗi rác bị dùng làm path ⇒ ghi ignore ra ngoài repo ⇒ mất
// ignore VĨNH VIỄN, im lặng (phase-01 §F3d).
export class GitOutputUnexpectedError extends Error {
  constructor(
    public readonly repoPath: string,
    public readonly rawOutput: string,
  ) {
    super(
      `git trả output bất thường khi resolve common dir cho "${repoPath}": ` +
        `${JSON.stringify(rawOutput)} (không phải đúng 1 dòng absolute path — có thể do ` +
        "git < 2.31 không hỗ trợ --path-format)",
    );
    this.name = "GitOutputUnexpectedError";
  }
}

// Absolute path tới git common dir.
//
// TIỀN ĐIỀU KIỆN (B5): CHỈ gọi hàm này sau khi `probeGitCli().status === "ok"`.
// Gộp "git không chạy được / quá cũ" vào cùng nghĩa `null` là CẤM — đó chính
// là lỗi khiến máy thiếu git bị báo NHẦM "không phải git repo" cho MỌI repo
// (phase-01 §Req F3b).
//
// `null` mang ĐÚNG MỘT nghĩa: "repoPath không phải git repo".
// Không dùng flag single-worktree (đã đo là SAI cho worktree — phase-01 §Key
// insight 3). Không có nhánh fallback thứ hai (KI-4/B9 — chưa đo cho worktree).
export function resolveGitCommonDir(repoPath: string): string | null {
  const result = spawnSync(
    "git",
    ["-C", repoPath, "rev-parse", "--path-format=absolute", "--git-common-dir"],
    { encoding: "utf8" },
  );

  if (result.error || result.status !== 0) {
    return null;
  }

  const out = (result.stdout ?? "").trim();

  // F3d — VALIDATE OUTPUT (lớp chặn thứ hai, sinh từ phép đo D6). git < 2.31
  // trả rc=0 KÈM RÁC (2 dòng, path tương đối) — không validate ⇒ mất ignore
  // VĨNH VIỄN, im lặng.
  if (out.includes("\n") || !isAbsolute(out)) {
    throw new GitOutputUnexpectedError(repoPath, out);
  }

  return out;
}
